/**
 * StandalonePOS - General Point of Sale System
 * Works without requiring an event - for campus shops, bookstores, cafeterias, etc.
 */
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

// UI Components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useOpsRole } from '@/hooks/useOpsRole';

// Icons
import {
  ShoppingCart, Plus, Minus, Trash2, Package, DollarSign, CreditCard,
  Banknote, Search, BarChart3, AlertTriangle, Loader2, Receipt,
  BookOpen, Shirt, Gift, Coffee, Disc, Edit, RefreshCw, X, Check,
  Smartphone, Monitor, Wrench, Home, Car, Utensils, Pill, Sparkles,
  Music, Camera, Headphones, Watch, Glasses, Briefcase, ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Extended categories for general retail
const CATEGORY_CONFIG = {
  books: { icon: BookOpen, label: 'Books & Media', color: 'bg-blue-500' },
  apparel: { icon: Shirt, label: 'Apparel', color: 'bg-purple-500' },
  accessories: { icon: Gift, label: 'Accessories', color: 'bg-pink-500' },
  food: { icon: Coffee, label: 'Food & Beverages', color: 'bg-amber-500' },
  electronics: { icon: Monitor, label: 'Electronics', color: 'bg-cyan-500' },
  home: { icon: Home, label: 'Home & Garden', color: 'bg-green-500' },
  automotive: { icon: Car, label: 'Automotive', color: 'bg-slate-500' },
  health: { icon: Pill, label: 'Health & Beauty', color: 'bg-rose-500' },
  music: { icon: Music, label: 'Music & Audio', color: 'bg-violet-500' },
  sports: { icon: Sparkles, label: 'Sports & Outdoors', color: 'bg-emerald-500' },
  tools: { icon: Wrench, label: 'Tools & Hardware', color: 'bg-orange-500' },
  other: { icon: Package, label: 'Other', color: 'bg-gray-500' }
};

const PAYMENT_METHODS = [
  { id: 'cash', name: 'Cash', icon: Banknote },
  { id: 'card', name: 'Card', icon: CreditCard },
  { id: 'mobile', name: 'Mobile Pay', icon: Smartphone },
];

// ============== Product Card ==============
const ProductCard = ({ product, onAddToCart, cartQty }) => {
  const config = CATEGORY_CONFIG[product.category] || CATEGORY_CONFIG.other;
  const CategoryIcon = config.icon;
  const isOutOfStock = product.quantity <= 0;
  const isLowStock = product.quantity > 0 && product.quantity <= (product.low_stock_threshold || 5);

  return (
    <Card 
      className={`cursor-pointer transition-all hover:shadow-md ${isOutOfStock ? 'opacity-50' : ''} ${cartQty > 0 ? 'ring-2 ring-primary' : ''}`}
      onClick={() => !isOutOfStock && onAddToCart(product)}
      data-testid={`product-card-${product.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-3 rounded-lg ${isOutOfStock ? 'bg-slate-100' : config.color + '/10'}`}>
            <CategoryIcon className={`h-6 w-6 ${isOutOfStock ? 'text-slate-400' : 'text-' + config.color.replace('bg-', '')}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-medium truncate">{product.name}</h4>
            <p className="text-xs text-muted-foreground truncate">{product.sku || 'No SKU'}</p>
            <div className="flex items-center justify-between mt-2">
              <span className="text-lg font-bold">${(product.price || 0).toFixed(2)}</span>
              <div className="flex items-center gap-2">
                {isLowStock && (
                  <Badge variant="outline" className="text-amber-600 border-amber-300">
                    Low Stock
                  </Badge>
                )}
                {isOutOfStock ? (
                  <Badge variant="secondary">Out of Stock</Badge>
                ) : (
                  <Badge variant="outline">{product.quantity} left</Badge>
                )}
              </div>
            </div>
            {cartQty > 0 && (
              <div className="mt-2 text-sm text-primary font-medium">
                {cartQty} in cart
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ============== Cart Item ==============
const CartItem = ({ item, onUpdateQty, onRemove }) => {
  return (
    <div className="flex items-center gap-3 py-2" data-testid={`cart-item-${item.id}`}>
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{item.name}</p>
        <p className="text-sm text-muted-foreground">${item.price.toFixed(2)} each</p>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => onUpdateQty(item.id, item.qty - 1)}>
          <Minus className="h-4 w-4" />
        </Button>
        <span className="w-8 text-center font-medium">{item.qty}</span>
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => onUpdateQty(item.id, item.qty + 1)}>
          <Plus className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => onRemove(item.id)}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <div className="w-20 text-right font-bold">
        ${(item.price * item.qty).toFixed(2)}
      </div>
    </div>
  );
};

// ============== Main Component ==============
const StandalonePOS = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('pos');
  // Role-gating — hide Products / Reports / Sales-history admin tabs
  // for cashiers. Backend enforces too (pos.manage / pos.sale / pos.refund).
  const { can, isSuper, loading: roleLoading } = useOpsRole('pos');
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showCheckout, setShowCheckout] = useState(false);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amountReceived, setAmountReceived] = useState('');
  const [customerName, setCustomerName] = useState('');
  // Customer-facing display session (random id, persisted until window closes)
  const displaySessionRef = useRef(null);
  const [displayOpen, setDisplayOpen] = useState(false);
  const ensureDisplaySession = () => {
    if (!displaySessionRef.current) {
      displaySessionRef.current = `pos-${Math.random().toString(36).slice(2, 10)}`;
    }
    return displaySessionRef.current;
  };
  const openCustomerDisplay = () => {
    const sid = ensureDisplaySession();
    setDisplayOpen(true);
    window.open(`/pos/display/${sid}`, 'pos_display',
      'popup,width=1280,height=720,noopener=false');
  };
  const askForTipOnDisplay = async () => {
    const sid = ensureDisplaySession();
    if (!displayOpen) openCustomerDisplay();
    try {
      const res = await fetch(
        `${API_URL}/api/pos/cart-mirror/${sid}/request-tip`,
        { method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ suggested_pct: [10, 15, 20], allow_custom: true }) }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Customer screen now showing tip prompt');
    } catch (e) {
      toast.error(`Could not request tip: ${e.message}`);
    }
  };
  const downloadDisplayPoster = async () => {
    const sid = ensureDisplaySession();
    try {
      const res = await fetch(
        `${API_URL}/api/pos/cart-mirror/${sid}/poster.pdf` +
          `?base_url=${encodeURIComponent(window.location.origin)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pos-display-poster-${sid}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Poster downloaded · print & post at the counter');
    } catch (e) {
      toast.error(`Could not generate poster: ${e.message}`);
    }
  };

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch products
  const { data: products = [], isLoading } = useQuery({
    queryKey: ['pos-products'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/pos/products`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Fetch sales stats
  const { data: stats = {} } = useQuery({
    queryKey: ['pos-stats'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/pos/stats`, { headers });
      if (!res.ok) return {};
      return res.json();
    }
  });

  // Add product mutation
  const addProductMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/admin/pos/products`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to add product');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['pos-products']);
      setShowAddProduct(false);
      toast.success('Product added');
    },
    onError: (error) => toast.error(error.message)
  });

  // Update product mutation
  const updateProductMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(`${API_URL}/api/admin/pos/products/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error('Failed to update product');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['pos-products']);
      setEditingProduct(null);
      toast.success('Product updated');
    },
    onError: (error) => toast.error(error.message)
  });

  // Create sale mutation
  const createSaleMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/admin/pos/sales`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to process sale');
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['pos-products']);
      queryClient.invalidateQueries(['pos-stats']);
      setCart([]);
      setShowCheckout(false);
      setCustomerName('');
      setAmountReceived('');
      toast.success(`Sale completed! Receipt #${data.receipt_number || data.id?.slice(-6)}`, {
        action: data.id ? {
          label: 'Print',
          onClick: async () => {
            try {
              const r = await fetch(`${API_URL}/api/pos/sales/${data.id}/receipt.html`,
                { headers: { Authorization: `Bearer ${token}` } });
              const html = await r.text();
              const w = window.open('', 'pos_receipt', 'popup,width=420,height=720');
              if (w) { w.document.write(html); w.document.close(); }
            } catch (e) { toast.error(`Print failed: ${e.message}`); }
          },
        } : undefined,
        duration: 8000,
      });
      // Reset customer display to fresh cart state for the next sale.
      if (displayOpen && displaySessionRef.current) {
        fetch(`${API_URL}/api/pos/cart-mirror/${displaySessionRef.current}/reset`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => {});
      }
    },
    onError: (error) => toast.error(error.message)
  });

  // Cart functions
  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        if (existing.qty >= product.quantity) {
          toast.error('Not enough stock');
          return prev;
        }
        return prev.map(item => 
          item.id === product.id ? { ...item, qty: item.qty + 1 } : item
        );
      }
      return [...prev, { ...product, qty: 1 }];
    });
  };

  const updateCartQty = (productId, newQty) => {
    if (newQty <= 0) {
      removeFromCart(productId);
      return;
    }
    const product = products.find(p => p.id === productId);
    if (product && newQty > product.quantity) {
      toast.error('Not enough stock');
      return;
    }
    setCart(prev => prev.map(item => 
      item.id === productId ? { ...item, qty: newQty } : item
    ));
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.id !== productId));
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.qty), 0);
  const change = amountReceived ? Math.max(0, parseFloat(amountReceived) - cartTotal) : 0;

  // Mirror cart to customer-facing display when one is open
  useEffect(() => {
    if (!displayOpen || !displaySessionRef.current) return;
    const sid = displaySessionRef.current;
    axios.post(
      `${API_URL}/api/pos/cart-mirror/${sid}`,
      {
        items: cart.map(it => ({ name: it.name, qty: it.qty, price: it.price })),
        subtotal: cartTotal,
        currency: 'USD',
        cashier_name: customerName || null,
      },
      { headers: { Authorization: `Bearer ${token}` } }
    ).catch(() => {});
  }, [cart, cartTotal, displayOpen, customerName, token]);

  // Filter products
  const filteredProducts = products.filter(p => {
    const matchesSearch = !searchTerm || 
      p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.sku?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || p.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Handle checkout
  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }
    if (paymentMethod === 'cash' && parseFloat(amountReceived || 0) < cartTotal) {
      toast.error('Insufficient payment amount');
      return;
    }
    createSaleMutation.mutate({
      items: cart.map(item => ({
        product_id: item.id,
        quantity: item.qty,
        unit_price: item.price
      })),
      payment_method: paymentMethod,
      amount_received: parseFloat(amountReceived) || cartTotal,
      customer_name: customerName || 'Walk-in Customer',
      total: cartTotal
    });
  };

  // Product Form Component
  const ProductForm = ({ product, onSubmit, onCancel }) => {
    const [formData, setFormData] = useState({
      name: product?.name || '',
      sku: product?.sku || '',
      category: product?.category || 'other',
      price: product?.price || '',
      quantity: product?.quantity || '',
      low_stock_threshold: product?.low_stock_threshold || 5,
      description: product?.description || ''
    });

    return (
      <form onSubmit={(e) => { e.preventDefault(); onSubmit(formData); }} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Product Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="Product name"
              data-testid="input-product-name"
            />
          </div>
          <div>
            <Label>SKU</Label>
            <Input
              value={formData.sku}
              onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
              placeholder="SKU-001"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Category</Label>
            <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    <div className="flex items-center gap-2">
                      <config.icon className="h-4 w-4" />
                      {config.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Price *</Label>
            <Input
              type="number"
              step="0.01"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              required
              placeholder="0.00"
              data-testid="input-product-price"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Quantity *</Label>
            <Input
              type="number"
              value={formData.quantity}
              onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
              required
              placeholder="0"
              data-testid="input-product-quantity"
            />
          </div>
          <div>
            <Label>Low Stock Alert</Label>
            <Input
              type="number"
              value={formData.low_stock_threshold}
              onChange={(e) => setFormData({ ...formData, low_stock_threshold: e.target.value })}
              placeholder="5"
            />
          </div>
        </div>
        <div>
          <Label>Description</Label>
          <Textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Product description..."
            rows={2}
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" data-testid="submit-product-btn">
            {product ? 'Update Product' : 'Add Product'}
          </Button>
        </DialogFooter>
      </form>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="standalone-pos">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <ShoppingCart className="h-6 w-6" />
                  Point of Sale
                </h1>
                <p className="text-sm text-muted-foreground">General retail & merchandise</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Today's Sales</p>
                <p className="text-xl font-bold text-green-600">${(stats.today_revenue || 0).toFixed(2)}</p>
              </div>
              <Badge variant="outline" className="text-lg px-3 py-1">
                {stats.today_transactions || 0} transactions
              </Badge>
              <Button variant="outline" size="sm" onClick={openCustomerDisplay}
                       data-testid="pos-open-customer-display">
                <Monitor className="h-4 w-4 mr-2" />
                {displayOpen ? 'Display live' : 'Customer display'}
              </Button>
              <Button variant="outline" size="sm" onClick={downloadDisplayPoster}
                       data-testid="pos-download-display-poster">
                <Receipt className="h-4 w-4 mr-2" />
                QR poster
              </Button>
              <Button variant="outline" size="sm" onClick={askForTipOnDisplay}
                       data-testid="pos-ask-for-tip"
                       className="border-amber-400 text-amber-700 hover:bg-amber-50">
                <DollarSign className="h-4 w-4 mr-2" />
                Ask for tip
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4" data-testid="pos-tabs">
            <TabsTrigger value="pos" data-testid="pos-tab-pos">
              <ShoppingCart className="h-4 w-4 mr-2" />
              POS
            </TabsTrigger>
            {(isSuper || can('pos.manage') || roleLoading) && (
              <TabsTrigger value="products" data-testid="pos-tab-products">
                <Package className="h-4 w-4 mr-2" />
                Products
              </TabsTrigger>
            )}
            {(isSuper || can('pos.refund') || can('pos.manage') || roleLoading) && (
              <TabsTrigger value="sales" data-testid="pos-tab-sales">
                <Receipt className="h-4 w-4 mr-2" />
                Sales History
              </TabsTrigger>
            )}
            {(isSuper || can('pos.manage') || roleLoading) && (
              <TabsTrigger value="reports" data-testid="pos-tab-reports">
                <BarChart3 className="h-4 w-4 mr-2" />
                Reports
              </TabsTrigger>
            )}
          </TabsList>

          {/* POS Tab */}
          <TabsContent value="pos">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Products Grid */}
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-4">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search products..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-9"
                          data-testid="search-products"
                        />
                      </div>
                      <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                        <SelectTrigger className="w-full max-w-[180px]">
                          <SelectValue placeholder="All Categories" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Categories</SelectItem>
                          {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                            <SelectItem key={key} value={key}>{config.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {isLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                      </div>
                    ) : filteredProducts.length === 0 ? (
                      <div className="text-center py-12">
                        <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">No products found</p>
                        <Button className="mt-4" onClick={() => setShowAddProduct(true)}>
                          <Plus className="h-4 w-4 mr-2" />
                          Add Product
                        </Button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                        {filteredProducts.map(product => (
                          <ProductCard
                            key={product.id}
                            product={product}
                            onAddToCart={addToCart}
                            cartQty={cart.find(i => i.id === product.id)?.qty || 0}
                          />
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Cart */}
              <div>
                <Card className="sticky top-20">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ShoppingCart className="h-5 w-5" />
                      Cart
                      {cart.length > 0 && (
                        <Badge variant="secondary">{cart.reduce((sum, i) => sum + i.qty, 0)} items</Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {cart.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <ShoppingCart className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>Cart is empty</p>
                        <p className="text-sm">Click products to add them</p>
                      </div>
                    ) : (
                      <>
                        <ScrollArea className="h-[300px]">
                          <div className="space-y-2">
                            {cart.map(item => (
                              <CartItem
                                key={item.id}
                                item={item}
                                onUpdateQty={updateCartQty}
                                onRemove={removeFromCart}
                              />
                            ))}
                          </div>
                        </ScrollArea>
                        <Separator className="my-4" />
                        <div className="space-y-2">
                          <div className="flex justify-between text-lg font-bold">
                            <span>Total</span>
                            <span>${cartTotal.toFixed(2)}</span>
                          </div>
                          <Button 
                            className="w-full" 
                            size="lg" 
                            onClick={() => setShowCheckout(true)}
                            data-testid="checkout-btn"
                          >
                            <CreditCard className="h-4 w-4 mr-2" />
                            Checkout
                          </Button>
                          <Button 
                            variant="outline" 
                            className="w-full" 
                            onClick={() => setCart([])}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Clear Cart
                          </Button>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Product Inventory</CardTitle>
                    <CardDescription>Manage your product catalog</CardDescription>
                  </div>
                  <Button onClick={() => setShowAddProduct(true)} data-testid="add-product-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Product
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead className="text-right">Stock</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.map(product => {
                      const config = CATEGORY_CONFIG[product.category] || CATEGORY_CONFIG.other;
                      return (
                        <TableRow key={product.id}>
                          <TableCell className="font-medium">{product.name}</TableCell>
                          <TableCell className="text-muted-foreground">{product.sku || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="gap-1">
                              <config.icon className="h-3 w-3" />
                              {config.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">${(product.price || 0).toFixed(2)}</TableCell>
                          <TableCell className="text-right">
                            {product.quantity <= (product.low_stock_threshold || 5) ? (
                              <Badge variant="destructive">{product.quantity}</Badge>
                            ) : (
                              product.quantity
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" onClick={() => setEditingProduct(product)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sales History Tab */}
          <TabsContent value="sales">
            <Card>
              <CardHeader>
                <CardTitle>Recent Sales</CardTitle>
                <CardDescription>Transaction history</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12 text-muted-foreground">
                  <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Sales history will appear here</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Today's Revenue</p>
                    <p className="text-3xl font-bold text-green-600">${(stats.today_revenue || 0).toFixed(2)}</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Today's Transactions</p>
                    <p className="text-3xl font-bold">{stats.today_transactions || 0}</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Total Products</p>
                    <p className="text-3xl font-bold">{products.length}</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Low Stock Items</p>
                    <p className="text-3xl font-bold text-amber-600">
                      {products.filter(p => p.quantity <= (p.low_stock_threshold || 5)).length}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Checkout Dialog */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Checkout</DialogTitle>
            <DialogDescription>Complete the sale</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Customer Name (optional)</Label>
              <Input
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Walk-in Customer"
              />
            </div>
            <div>
              <Label>Payment Method</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                {PAYMENT_METHODS.map(method => (
                  <Button
                    key={method.id}
                    variant={paymentMethod === method.id ? 'default' : 'outline'}
                    className="flex flex-col items-center gap-1 h-auto py-3"
                    onClick={() => setPaymentMethod(method.id)}
                    data-testid={`payment-${method.id}`}
                  >
                    <method.icon className="h-5 w-5" />
                    <span className="text-xs">{method.name}</span>
                  </Button>
                ))}
              </div>
            </div>
            {paymentMethod === 'cash' && (
              <div>
                <Label>Amount Received</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={amountReceived}
                  onChange={(e) => setAmountReceived(e.target.value)}
                  placeholder="0.00"
                  data-testid="amount-received"
                />
                {parseFloat(amountReceived) >= cartTotal && (
                  <p className="text-sm text-green-600 mt-1">
                    Change: ${change.toFixed(2)}
                  </p>
                )}
              </div>
            )}
            <Separator />
            <div className="flex justify-between text-lg font-bold">
              <span>Total</span>
              <span>${cartTotal.toFixed(2)}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckout(false)}>Cancel</Button>
            <Button 
              onClick={handleCheckout} 
              disabled={createSaleMutation.isPending}
              data-testid="complete-sale-btn"
            >
              {createSaleMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Complete Sale
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Product Dialog */}
      <Dialog open={showAddProduct} onOpenChange={setShowAddProduct}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Product</DialogTitle>
          </DialogHeader>
          <ProductForm
            onSubmit={(data) => addProductMutation.mutate(data)}
            onCancel={() => setShowAddProduct(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={!!editingProduct} onOpenChange={(open) => !open && setEditingProduct(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Product</DialogTitle>
          </DialogHeader>
          {editingProduct && (
            <ProductForm
              product={editingProduct}
              onSubmit={(data) => updateProductMutation.mutate({ id: editingProduct.id, data })}
              onCancel={() => setEditingProduct(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StandalonePOS;
